import { useState, type ReactNode } from 'react';
import type { KioskConfigDTO } from '@print-saas/shared';
import { CheckIcon } from './icons';

type Printer = KioskConfigDTO['printer'];

interface PrinterPickerProps {
  printer: Printer;
  /** Saves the choice; rejects with a message to show. */
  onSelect: (name: string) => Promise<void>;
  /** Owner preview: show the list without letting anyone change it. */
  readOnly?: boolean;
}

/** The printers installed on the kiosk computer, as reported by the agent, to choose from. */
export function PrinterPicker({ printer, onSelect, readOnly = false }: PrinterPickerProps) {
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!printer.agentOnline) {
    return (
      <Note tone="bad">
        The kiosk agent isn’t running on this computer, so its printers can’t be listed. Start the agent and
        they appear here.
      </Note>
    );
  }
  if (printer.driver === 'mock') {
    return (
      <Note tone="warn">
        Test mode: the kiosk agent is set to <code className="font-mono">PRINTER_DRIVER=mock</code>, so
        nothing is printed. Remove that setting to use a real printer.
      </Note>
    );
  }

  const choose = async (name: string) => {
    if (readOnly || saving !== null) return;
    setSaving(name);
    setError(null);
    try {
      await onSelect(name);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not set the printer');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="flex w-full flex-col gap-[1.4vmin]">
      {printer.detected.length === 0 && <Note tone="warn">{emptyListNote(printer)}</Note>}
      {printer.detected.map((p) => (
        <Option
          key={p.name}
          title={p.description ?? p.name}
          detail={p.description ? p.name : null}
          badge={p.isDefault ? "Computer's default" : null}
          selected={printer.selected === p.name}
          saving={saving === p.name}
          disabled={readOnly}
          onClick={() => void choose(p.name)}
        />
      ))}
      {error && <Note tone="bad">{error}</Note>}
    </div>
  );
}

function Option({
  title,
  detail,
  badge,
  selected,
  saving,
  disabled,
  onClick,
}: {
  title: string;
  detail: string | null;
  badge: string | null;
  selected: boolean;
  saving: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={`flex w-full items-center gap-[2vmin] rounded-[2vmin] px-[3vmin] py-[2.2vmin] text-left ring-2 transition-colors ${
        selected ? 'bg-accent/12 ring-accent' : 'bg-white/5 ring-line active:bg-white/10'
      } ${saving ? 'animate-pulse' : ''}`}
    >
      <span
        className={`grid size-[4.4vmin] shrink-0 place-items-center rounded-full ring-2 ${
          selected ? 'bg-accent text-accent-ink ring-accent' : 'ring-line'
        }`}
      >
        {selected && <CheckIcon className="size-[3vmin]" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[2.8vmin] font-bold">{title}</span>
        {detail && <span className="block truncate text-[2vmin] text-muted">{detail}</span>}
      </span>
      {badge && (
        <span className="shrink-0 rounded-full bg-go/15 px-[1.6vmin] py-[0.5vmin] text-[1.8vmin] font-semibold text-go">
          {badge}
        </span>
      )}
    </button>
  );
}

function Note({ tone, children }: { tone: 'warn' | 'bad'; children: ReactNode }) {
  return (
    <p
      className={`rounded-[1.6vmin] px-[2.4vmin] py-[1.6vmin] text-left text-[2.2vmin] leading-snug ring-1 ${
        tone === 'bad' ? 'bg-bad/10 text-bad ring-bad/40' : 'bg-warn/10 text-warn ring-warn/40'
      }`}
    >
      {children}
    </p>
  );
}

/** Why there is nothing to choose, as far as the agent could tell. */
function emptyListNote(printer: KioskConfigDTO['printer']) {
  const os = printer.driver === 'cups' ? 'CUPS' : 'Windows';
  if (printer.listError) return `Couldn’t ask ${os} for its printers: ${printer.listError}. The kiosk keeps trying.`;
  if (printer.hiddenCount) {
    return `Only PDF/XPS printers found (${printer.hiddenCount}): they can’t print on paper. Install the kiosk printer first; it appears here within a minute.`;
  }
  return printer.driver === 'cups'
    ? 'No printers found on this computer. Add the printer in CUPS first; it appears here within a minute.'
    : 'No printers found on this computer. Install the printer in Settings › Bluetooth & devices › Printers & scanners, signed in as this kiosk’s Windows user. It appears here within a minute.';
}
